import {createCodeEditor, importCodemirror} from '../modules/codeeditor/main.ts';
import type {CodemirrorModules} from '../modules/codeeditor/main.ts';
import {registerGlobalInitFunc} from '../modules/observer.ts';
import type {EditorState, Text} from '@codemirror/state';
import type {DecorationSet, EditorView} from '@codemirror/view';

// A conflict block in a file with conflict markers, all fields are 1-based line numbers:
//   <<<<<<< head-branch    (start)
//   current change
//   ||||||| merge-base     (base, only with the "diff3" conflict style)
//   common ancestor
//   =======                (separator)
//   incoming change
//   >>>>>>> base-branch    (end)
export type ConflictBlock = {start: number; base: number | null; separator: number; end: number};

export type ConflictChoice = 'current' | 'incoming' | 'both';

const reStartMarker = /^<{7}(?: |$)/;
const reBaseMarker = /^\|{7}(?: |$)/;
const reSeparatorMarker = /^={7}\s*$/;
const reEndMarker = /^>{7}(?: |$)/;

export function findConflictBlocks(lines: string[]): ConflictBlock[] {
  const blocks: ConflictBlock[] = [];
  let start = 0, base = 0, separator = 0;
  for (const [idx, line] of lines.entries()) {
    const lineNum = idx + 1;
    if (reStartMarker.test(line)) {
      start = lineNum;
      base = 0;
      separator = 0;
    } else if (start && !separator && !base && reBaseMarker.test(line)) {
      base = lineNum;
    } else if (start && !separator && reSeparatorMarker.test(line)) {
      separator = lineNum;
    } else if (start && separator && reEndMarker.test(line)) {
      blocks.push({start, base: base || null, separator, end: lineNum});
      start = 0;
    }
  }
  return blocks;
}

// hasConflictMarkers reports whether the lines still contain any start or end marker, the backend refuses such content
export function hasConflictMarkers(lines: string[]): boolean {
  return lines.some((line) => reStartMarker.test(line) || reEndMarker.test(line));
}

// resolveConflictBlock returns the lines which replace the whole block (markers included) for the given choice
export function resolveConflictBlock(lines: string[], block: ConflictBlock, choice: ConflictChoice): string[] {
  const current = lines.slice(block.start, (block.base ?? block.separator) - 1);
  const incoming = lines.slice(block.separator, block.end - 1);
  if (choice === 'current') return current;
  if (choice === 'incoming') return incoming;
  return [...current, ...incoming];
}

type ConflictTexts = {acceptCurrent: string; acceptIncoming: string; acceptBoth: string};

function docLines(doc: Text): string[] {
  return doc.toJSON();
}

function acceptConflict(view: EditorView, index: number, choice: ConflictChoice) {
  const doc = view.state.doc;
  const lines = docLines(doc);
  const block = findConflictBlocks(lines)[index];
  if (!block) return;
  const replacement = resolveConflictBlock(lines, block, choice);
  let from = doc.line(block.start).from;
  let to = doc.line(block.end).to;
  if (!replacement.length) {
    // remove the whole block including one line break, so no empty line is left behind
    if (block.end < doc.lines) {
      to++;
    } else if (from > 0) {
      from--;
    }
  }
  view.dispatch({changes: {from, to, insert: replacement.join('\n')}, userEvent: 'input.conflict'});
  view.focus();
}

function conflictExtension(cm: CodemirrorModules, texts: ConflictTexts) {
  const {Decoration, EditorView, WidgetType} = cm.view;

  class ConflictActionsWidget extends WidgetType {
    index: number;
    constructor(index: number) {
      super();
      this.index = index;
    }
    eq(other: ConflictActionsWidget) {
      return other.index === this.index;
    }
    toDOM(view: EditorView) {
      const el = document.createElement('div');
      el.className = 'cm-conflict-actions';
      const actions: [ConflictChoice, string][] = [
        ['current', texts.acceptCurrent],
        ['incoming', texts.acceptIncoming],
        ['both', texts.acceptBoth],
      ];
      for (const [choice, label] of actions) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'cm-conflict-action';
        btn.textContent = label;
        btn.addEventListener('click', () => acceptConflict(view, this.index, choice));
        el.append(btn);
      }
      return el;
    }
  }

  const lineDeco = (cls: string) => Decoration.line({class: cls});
  const buildDecorations = (state: EditorState): DecorationSet => {
    const doc = state.doc;
    const decos = [];
    for (const [index, block] of findConflictBlocks(docLines(doc)).entries()) {
      const startLine = doc.line(block.start);
      decos.push(Decoration.widget({widget: new ConflictActionsWidget(index), block: true, side: -1}).range(startLine.from));
      for (let n = block.start; n <= block.end; n++) {
        let cls = 'cm-conflict-incoming';
        if (n === block.start || n === block.separator || n === block.end || n === block.base) {
          cls = 'cm-conflict-marker';
        } else if (n < (block.base ?? block.separator)) {
          cls = 'cm-conflict-current';
        } else if (n < block.separator) {
          cls = 'cm-conflict-base';
        }
        decos.push(lineDeco(cls).range(doc.line(n).from));
      }
    }
    return Decoration.set(decos, true);
  };

  const field = cm.state.StateField.define<DecorationSet>({
    create: (state) => buildDecorations(state),
    update: (decos, tr) => (tr.docChanged ? buildDecorations(tr.state) : decos),
    provide: (f) => EditorView.decorations.from(f),
  });
  return field;
}

function initRepoPullConflicts(form: HTMLFormElement) {
  const texts: ConflictTexts = {
    acceptCurrent: form.getAttribute('data-text-accept-current')!,
    acceptIncoming: form.getAttribute('data-text-accept-incoming')!,
    acceptBoth: form.getAttribute('data-text-accept-both')!,
  };
  const textRemaining = form.getAttribute('data-text-conflicts-remaining')!;
  const textResolved = form.getAttribute('data-text-resolved')!;
  const textAllResolved = form.getAttribute('data-text-all-resolved')!;
  const submitButton = form.querySelector<HTMLButtonElement>('.conflicts-submit')!;
  const summary = form.querySelector<HTMLElement>('.conflicts-summary')!;

  const fileStates = new Map<HTMLElement, {remaining: number; unresolved: boolean}>();
  const updateSummary = () => {
    let remaining = 0, unresolved = false;
    for (const state of fileStates.values()) {
      remaining += state.remaining;
      unresolved = unresolved || state.unresolved;
    }
    const allLoaded = fileStates.size === form.querySelectorAll('.conflict-file').length;
    submitButton.disabled = !allLoaded || unresolved;
    summary.textContent = unresolved ? textRemaining.replace('{count}', String(Math.max(remaining, 1))) : textAllResolved;
  };

  const updateFileStatus = (fileEl: HTMLElement, doc: Text) => {
    const lines = docLines(doc);
    const remaining = findConflictBlocks(lines).length;
    const unresolved = hasConflictMarkers(lines);
    fileStates.set(fileEl, {remaining, unresolved});
    const label = fileEl.querySelector<HTMLElement>('.conflict-file-status')!;
    label.textContent = unresolved ? textRemaining.replace('{count}', String(Math.max(remaining, 1))) : textResolved;
    label.classList.toggle('red', unresolved);
    label.classList.toggle('green', !unresolved);
    updateSummary();
  };

  (async () => {
    const cm = await importCodemirror();
    for (const fileEl of form.querySelectorAll<HTMLElement>('.conflict-file')) {
      const textarea = fileEl.querySelector<HTMLTextAreaElement>('textarea[name="content"]')!;
      const editor = await createCodeEditor(textarea);
      editor.view.dispatch({
        effects: cm.state.StateEffect.appendConfig.of([
          conflictExtension(cm, texts),
          cm.view.EditorView.updateListener.of((update) => {
            if (update.docChanged) updateFileStatus(fileEl, update.state.doc);
          }),
        ]),
      });
      updateFileStatus(fileEl, editor.view.state.doc);
    }
  })();
}

export function initRepoPullConflictsForm() {
  registerGlobalInitFunc('initRepoPullConflicts', initRepoPullConflicts);
}
